/* Custom: colour swatches (sibling products from the custom.variants metafield) are
   plain links to the sibling PDP — correct on the product page, but inside the
   quick-buy drawer they navigated away. Intercept those clicks and reload the drawer
   with the sibling product instead: QuickBuyDrawer.show() re-fetches
   /products/<handle> and re-renders header (image/title/price), sizes, swatches and
   buy buttons whenever _hasLoaded is false.

   Smoothness: the fetch swap is masked by an opacity fade on the drawer's shadow
   parts (see custom-base.css [aria-busy] rules — the light-DOM children are
   display:contents, so they can't fade) plus a height tween between the old and new
   content, mirroring the theme's own variant-added transition. The swatch strip is
   fresh DOM after the swap, so its scroll position is restored (and nudged so the
   newly active colour stays in view — sibling order can differ between products). */
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The swap mutates the drawer's handle, so without a reset a reopened drawer would
     show the last colour browsed instead of the product on its card. Capture the
     card's own handle on first swap and restore it whenever the drawer closes. */
  const resetHandleOnClose = (drawer) => {
    if (drawer._colorSwapObserved) return;
    drawer._colorSwapObserved = true;

    const originalHandle = drawer.getAttribute('handle');
    new MutationObserver(() => {
      if (!drawer.hasAttribute('open') && drawer.getAttribute('handle') !== originalHandle) {
        drawer.setAttribute('handle', originalHandle);
        drawer._hasLoaded = false; // next show() re-fetches the card's product
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  };

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('.color-swatch-main-container a[href]');
    if (!link) return;

    const drawer = link.closest('quick-buy-drawer');
    if (!drawer) return;

    const match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;

    event.preventDefault();
    if (drawer.getAttribute('aria-busy') === 'true') return; // a swap is already in flight

    const savedScroll = link.closest('.swatch-row') ? link.closest('.swatch-row').scrollLeft : 0;
    const content = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="content"]');
    const fromHeight = content ? content.clientHeight : null;

    resetHandleOnClose(drawer);
    drawer.setAttribute('handle', match[1]);
    drawer._hasLoaded = false;
    await drawer.show();

    const row = drawer.querySelector('.swatch-row');
    if (row) {
      row.scrollLeft = savedScroll;

      const activeLink = row.querySelector('.active') && row.querySelector('.active').closest('a');
      if (activeLink) {
        const rowRect = row.getBoundingClientRect();
        const activeRect = activeLink.getBoundingClientRect();
        if (activeRect.left < rowRect.left || activeRect.right > rowRect.right) {
          row.scrollLeft += (activeRect.left + activeRect.width / 2) - (rowRect.left + rowRect.width / 2);
        }
      }
    }

    if (content && fromHeight !== null && !REDUCED_MOTION.matches) {
      const toHeight = content.clientHeight;
      if (Math.abs(toHeight - fromHeight) > 1) {
        content.animate(
          { height: [`${fromHeight}px`, `${toHeight}px`] },
          { duration: 300, easing: 'cubic-bezier(0.86, 0, 0.07, 1)' }
        );
      }
    }
  });
})();
